import React, { useEffect, useState, useRef } from "react";
import { css } from "@emotion/react";
import { stringify } from "qs";
import { decamelizeKeys } from "humps";
import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter } from "next/router";
import isEqual from "react-fast-compare";
import { ImageList } from "main/javascripts/components/spot/ImageList";
import { Header } from "main/javascripts/components/molecules/Spot/Header";
import { Description } from "main/javascripts/components/molecules/Spot/Description";
import { Location } from "main/javascripts/components/spot/Location";
import { ReviewList } from "main/javascripts/components/spot/ReviewList";
import { BasicBlock } from "main/javascripts/components/atoms/Block/BasicBlock";
import { MainBlock } from "main/javascripts/components/atoms/Block/MainBlock";
import { MainStyle } from "main/javascripts/components/organisms/Hotel/Show/MainStyle";
import { HotelRoomListWithSelectedPlan } from "./hotelRoom/HotelRoomListWithSelectedPlan";
import { HotelDetails } from "main/javascripts/features/hotel/components/HotelDetails";
import { DynamicPackageStepIndicator } from "main/javascripts/components/molecules/DynamicPackage/DynamicPackageStepIndicator";
import { SearchModalDialog } from "main/javascripts/components/molecules/Modal/SearchModalDialog/SearchModalDialog";
import { HotelRoomList } from "./hotelRoom/HotelRoomList";
import { HotelImageModal } from "./HotelImageModal";
import { HotelRoomDetailModal } from "./hotelRoom/HotelRoomDetailModal";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import { postResearchDynamicPackageCache } from "main/javascripts/features/dynamicPackage/searchDynamicPackageSlice";
import {
  fetchHotelRooms,
  hotelSelector,
  selectedHotelPlanSelector,
  setDisplayHotelImageModal,
  setIsLoadingRoom,
  setRequestRoomError,
} from "../hotelSlice";
import {
  hotelShowParamSelector,
  hotelShowParamToApiQueryParam,
  initHotelShowParam,
  isCacheIdExpired,
} from "main/javascripts/slices/paramSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";

const HotelShowMainComponent: React.FC = (): React.ReactElement => {
  const appDispatch = useAppDispatch();
  const router = useRouter();

  const hotel = useAppSelector(hotelSelector);
  const param = useAppSelector(hotelShowParamSelector);
  const selectedHotelPlan = useAppSelector(selectedHotelPlanSelector);
  const isDynamicPackage: boolean = !!param.dynamicPackageId;
  const expirationTimerRef = useRef(null);
  const [isExpired, setIsExpired] = useState(false);

  const checkExpiration = (): void => {
    if (isCacheIdExpired(param.dynamicPackageId)) {
      setIsExpired(true);
    }
  };

  useEffect(() => {
    if (!param.valid) {
      // appDispatch(setRequestRoomError({ errors: {} })); // null以外を設定
      return;
    }
    if (isDynamicPackage) {
      expirationTimerRef.current = setInterval(() => {
        checkExpiration();
      }, 10000);
      checkExpiration();
    }

    appDispatch(setIsLoadingRoom(true));
    appDispatch(setRequestRoomError(null));
    appDispatch(
      fetchHotelRooms({
        hotelId: hotel.id,
        param: hotelShowParamToApiQueryParam(param),
      })
    )
      .then(unwrapResult)
      .then((result) => {
        appDispatch(
          initHotelShowParam({
            ...param,
            roomCacheId: result.roomCacheId,
          })
        );
        appDispatch(setIsLoadingRoom(false));
      })
      .catch((error: any) => {
        appDispatch(setRequestRoomError(error));
        appDispatch(setIsLoadingRoom(false));
      });
    return () => {
      if (isDynamicPackage) {
        clearInterval(expirationTimerRef.current);
      }
    };
  }, []);

  const onImageListClick = () => {
    appDispatch(setDisplayHotelImageModal(true));
  };
  const refreshHandler: () => void = async () => {
    return appDispatch(
      postResearchDynamicPackageCache({
        cacheId: param.dynamicPackageId,
      })
    )
      .then(unwrapResult)
      .then((result) => {
        router.push(
          `/flights?${stringify(decamelizeKeys(result.data.conditions), {
            arrayFormat: "indices",
          })}&cache_id=${result.data.cacheId}&dynamic_package_id=${
            result.data.dynamicPackageId
          }&search_group_id=${result.data.searchGroupId}&search_type=dp`
        );
      });
  };

  const imageListStyles = { exBlockStyle: imageListBlockStyle };

  return (
    <MainBlock>
      {isDynamicPackage ? (
        <DynamicPackageStepIndicator
          step={3}
          styles={{ blockStyle: MainStyle.stepIndicatorBlock }}
        />
      ) : (
        ""
      )}
      <div css={imageBlockStyle}>
        <ImageList
          images={hotel.hotelImages}
          styles={imageListStyles}
          onClick={onImageListClick}
        />
      </div>
      <BasicBlock>
        <div css={basicInfoBlockStyle}>
          <Header
            name={hotel.name}
            address={hotel.address}
            rating={hotel.rating}
          />
          <Description description={hotel.description} />
          {hotel.isEan ? (
            <Location
              address={hotel.address}
              name={hotel.name}
              latitude={hotel.latitude}
              longitude={hotel.longitude}
            />
          ) : (
            ""
          )}
        </div>
      </BasicBlock>
      {!selectedHotelPlan ? (
        <HotelRoomList />
      ) : (
        <HotelRoomListWithSelectedPlan />
      )}
      <BasicBlock>
        <HotelDetails hotel={hotel} />
      </BasicBlock>
      <BasicBlock styles={MainStyle.reviewListBlock}>
        <ReviewList reviews={hotel.googleReviews} rating={hotel.rating} />
      </BasicBlock>
      <HotelImageModal />
      <HotelRoomDetailModal />
      <SearchModalDialog
        displayed={isDynamicPackage && isExpired}
        refreshHandler={refreshHandler}
      />
    </MainBlock>
  );
};

const areEqual: any = (prevProps: any, nextProps: any): any => {
  return isEqual(prevProps, nextProps);
};
export const HotelShowMain: any = React.memo(HotelShowMainComponent, areEqual);

const imageBlockStyle = css`
  cursor: pointer;
  padding: 0 0 ${space.block2x} 0;
  max-width: 100%;
  box-sizing: border-box;
`;
const imageListBlockStyle = css`
  padding: 0 0 ${space.atom};
`;
const basicInfoBlockStyle = css`
  margin: -${space.atom} 0;
`;
// const reviewListBlockStyle = css`
//   padding: 0 ${space.atom} ${space.atom3x} ${space.atom};
//   &:before {
//     content: "";
//     display: block;
//     border-top: 1px solid ${accentColor.primaryColor};
//     margin: 0 ${space.atom} ${space.atom3x};
//   }
// `;
// const stepIndicatorBlockStyle = css`
//   padding-bottom: ${space.atom3x};
// `;
